import React, { useState, useMemo } from 'react';
import { getNumericRef, DEPENDENCY_COLOURS } from './GanttUtils';

// A link whose source is closed is INERT: buildBlockedTasksMap only raises a
// dependency blocker while the source is open, so a dead link constrains
// nothing. It therefore must not compete for attention - pale and dashed at
// rest. Hovering solidifies the dash so the link can be traced end to end, but
// the colour and weight do not change: it stays pale at 2px, never reverting to
// its own colour. Isolation dimming the other lines to 25% is what shows which
// link is under the cursor.
const DEAD_COLOUR = '#cbd5e1';   // slate-300
const DEAD_DASH = '5 4';

// Neutral grey for the fully-squished zoom mode ONLY, where stacked colours are
// a mess. Deliberately NOT the dead-link styling: these are different meanings
// and used to share one colour, which is why a completed link read as heavier
// than several live ones (slate-500 is darker than the yellow, cyan and teal
// dependency colours).
const SQUISH_COLOUR = '#64748b'; // slate-500

// Lanes renderer - the sole dependency-line renderer (chosen 2026-07-29;
// the old z-fighting renderer, its dash-stagger/merge machinery and the
// 'bundled' exploration mode are gone). Behaviours:
//
// - Corridor grouping: links sharing a departure vertical ride parallel lanes
// - Zoom-driven squish: full comb at 400%+ slider zoom, shrinking below,
//   completely single (overlapped) line at 250% and under
// - Least-occlusion corridor placement: the comb scans for the clearest
//   vertical channel right of the source instead of a fixed offset
// - Crossing-free ordering: departure corridor gives the shortest hop the
//   outermost lane; descent corridor gives the first box the closest lane -
//   no line ever cuts through a sibling
// - Port fan-out / arrival fan-in spread lines across the bar height at each
//   end, auto-compressed to line count
// - No-gap hops take a COLLAPSED U-turn under the source row (deliberately
//   overlapped) and separate only at the descent corridor; a corridor's shared
//   turn row applies ONLY to links that travel down to or past it, and a turn
//   never goes below the link's own target row
// - Rounded corners, radius auto-shrunk on short segments
// - Hover isolation: the hovered link's WHOLE CHAIN stays full colour (hovered
//   link 3px, rest of the chain 2px), everything else dims to 25%
// - Dead links (source closed) are pale and dashed; hover solidifies the dash
//   only - never the colour or the weight
const DependencyArrows = ({
    effectiveLinks,
    rows,
    readOnly,
    headerHeight,
    currentScale,
    getDisplayPosition,
    getDateGap,
    onDependencyDelete,
    onDependencyColorChange,
    setColorPopup,
    setPendingLinks,
    leftPanelWidth,
    timelineWidth,
    rowHeight,
    zoomLevel,
    // Optional: parent shows its dark CustomTooltip for the hovered link.
    // Called with { content, x, y } on enter, null on leave.
    onLinkHover
}) => {
    // Hover isolation state - top-level hook, before any early return
    const [hoveredLinkKey, setHoveredLinkKey] = useState(null);

    // Adjacency for chain highlighting. Also a hook, so also before the early
    // return, and rebuilt only when the link set changes - not per hover.
    //
    // A node is PROJECT + numeric ref, never the ref alone: a main template and
    // an attached add-in both number their subtasks from 1, so a bare ref would
    // let a chain silently jump between them (the collision fixed in c2fbcb0).
    // Links spell their two ends differently - source is a numeric ref, target
    // is a task ID - so both go through getNumericRef to be comparable.
    const graph = useMemo(() => {
        const nodeOf = (projectId, ref) => `${projectId || ''}#${ref}`;
        const outbound = new Map();
        const inbound = new Map();
        const edges = [];
        (effectiveLinks || []).forEach(link => {
            const sourceNode = nodeOf(link.sourceProjectId, getNumericRef(link.source));
            const targetNode = nodeOf(link.targetProjectId, getNumericRef(link.target));
            const edge = { chainKey: `${sourceNode}->${targetNode}`, sourceNode, targetNode };
            if (!outbound.has(sourceNode)) outbound.set(sourceNode, []);
            if (!inbound.has(targetNode)) inbound.set(targetNode, []);
            outbound.get(sourceNode).push(edge);
            inbound.get(targetNode).push(edge);
            edges.push(edge);
        });
        return { nodeOf, outbound, inbound, edges };
    }, [effectiveLinks]);

    if (effectiveLinks.length === 0) return null;

    const headerOffset = headerHeight || (currentScale !== 'month' ? 64 : 36);
    const stubLength = 15;

    // Dead links keep their resting weight when hovered - the 3px promotion is
    // for links that still constrain something. Only the link actually under
    // the cursor thickens; the rest of its chain stays 2px in full colour, so
    // the chain reads as one run while you can still see which link you are on.
    const strokeW = (r) => (hoveredLinkKey === r.key && !r.sourceClosed ? 3 : 2);

    // Dark detail card content for a hovered line. The tree replaces the old
    // source / "finish to start" / target pair: the highlighting now covers a
    // whole chain, so the card has to describe the same thing.
    const linkTooltip = (r, hiddenCount = 0, tree = null) => (
        <div>
            <div className="font-semibold mb-1 flex items-center gap-2">
                <span
                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: r.sourceClosed ? DEAD_COLOUR : r.colour }}
                />
                {r.sourceClosed ? 'Dependency — no longer active' : 'Dependency'}
            </div>
            <div className="text-gray-400 text-xs mb-1">finish to start</div>
            {(tree?.lines || []).map((l, i) => (
                <div
                    key={`${l.node}_${i}`}
                    className={`flex items-start ${
                        l.repeat ? 'text-gray-500'
                            : l.current ? 'font-semibold text-white'
                                : 'text-gray-200'
                    }`}
                >
                    {/* Guides are monospace and whitespace-pre so the vertical
                        runs line up under each other - the card's own font is
                        proportional and would stagger them. */}
                    {(l.guides.length > 0 || l.connector) && (
                        <span className="font-mono text-gray-500 whitespace-pre flex-shrink-0">
                            {l.guides.map(g => (g === 'line' ? '│  ' : '   ')).join('')}
                            {l.connector === 'tee' ? '├─ ' : l.connector === 'ell' ? '└─ ' : ''}
                        </span>
                    )}
                    <span>{l.repeat ? '↩ ' : ''}{l.label}</span>
                </div>
            ))}
            {tree?.more > 0 && (
                <div className="text-gray-500 text-xs mt-0.5">+ {tree.more} more</div>
            )}
            {/* The state of the LINK, not of the source task. The old wording
                ("Source task completed") described the wrong object, in the
                faintest style in the card, so the one fact that matters read as
                a footnote. */}
            {r.sourceClosed && (
                <div className="mt-1.5 pt-1.5 border-t border-gray-700 text-gray-300">
                    Source is closed, so this no longer constrains the target.
                </div>
            )}
            {/* A hop whose endpoints are not both visible subtask rows cannot be
                drawn (see the resolve filter), so the highlight would just stop
                mid-chain with no explanation. Say so instead. */}
            {hiddenCount > 0 && (
                <div className="mt-1.5 pt-1.5 border-t border-gray-700 text-gray-300">
                    Chain continues through {hiddenCount} hidden
                    {hiddenCount === 1 ? ' link' : ' links'}.
                </div>
            )}
        </div>
    );

    const hoverProps = (r) => ({
        onMouseEnter: (e) => {
            setHoveredLinkKey(r.key);
            const c = chainFor(r);
            onLinkHover?.({
                content: linkTooltip(r, c.hidden, chainTree(r, c.keys)),
                x: e.clientX,
                y: e.clientY,
                // Node keys of every task in the chain, so the parent can dim
                // the bars that are not part of it. The parent already re-renders
                // on this call to place the tooltip, so this costs no extra pass.
                nodes: chainNodes(c.keys)
            });
        },
        onMouseLeave: () => {
            setHoveredLinkKey(null);
            onLinkHover?.(null);
        }
    });

    const svgStyle = {
        width: leftPanelWidth + timelineWidth,
        height: rows.length * rowHeight + 80,
        zIndex: 5,
        overflow: 'visible',
        transform: `translateZ(0) translateX(${(zoomLevel || 1) * 0.001}px)`
    };

    // Zoom-percentage driven geometry (Bill, 2026-07-29): full comb at 400%+,
    // shrinking below that, completely single line at 250% and under
    const zoomPct = (zoomLevel || 1) * 100;
    const s = Math.max(0, Math.min(1, (zoomPct - 250) / 150));

    const laneStepMax = 4 * s;
    const corridorCap = 24 * s;
    const cornerR = Math.max(2, 6 * s);
    const scanMin = Math.max(4, 6 * s);
    const scanMax = Math.max(scanMin + 1, 40 * s);
    const fanStepMax = 5;    // px between departure/arrival ports
    const fanSpanMax = 16;   // total fan height, stays inside the bar

    // Orthogonal path with quarter-arc corners; radius shrinks on segments
    // shorter than 2r so tight elbows never overdraw
    const roundedPath = (pts, radius) => {
        let d = `M ${pts[0][0]} ${pts[0][1]}`;
        for (let i = 1; i < pts.length - 1; i++) {
            const [px, py] = pts[i - 1];
            const [cx, cy] = pts[i];
            const [nx, ny] = pts[i + 1];
            const inLen = Math.hypot(cx - px, cy - py);
            const outLen = Math.hypot(nx - cx, ny - cy);
            const rr = Math.min(radius, inLen / 2, outLen / 2);
            if (rr < 0.5) {
                d += ` L ${cx} ${cy}`;
                continue;
            }
            const inX = cx - Math.sign(cx - px) * rr;
            const inY = cy - Math.sign(cy - py) * rr;
            const outX = cx + Math.sign(nx - cx) * rr;
            const outY = cy + Math.sign(ny - cy) * rr;
            d += ` L ${inX} ${inY} Q ${cx} ${cy} ${outX} ${outY}`;
        }
        const [lx, ly] = pts[pts.length - 1];
        return d + ` L ${lx} ${ly}`;
    };

    // Centred fan offsets for n ports, auto-compressed to the span
    const fanOffsets = (n, stepMax = fanStepMax, spanMax = fanSpanMax) => {
        if (n < 2) return [0];
        const step = Math.min(stepMax, spanMax / (n - 1));
        return Array.from({ length: n }, (_, i) => (i - (n - 1) / 2) * step);
    };

    // Resolve every link once
    const resolved = [];
    effectiveLinks.forEach(link => {
        const sourceRowIdx = rows.findIndex(r =>
            r.ref === getNumericRef(link.source) &&
            r.type === 'subtask' &&
            (!link.sourceProjectId || (r.sourceTemplateId || r.projectId) === link.sourceProjectId)
        );
        const targetRowIdx = rows.findIndex(r =>
            r.ref === getNumericRef(link.target) &&
            r.type === 'subtask' &&
            (!link.targetProjectId || (r.sourceTemplateId || r.projectId) === link.targetProjectId)
        );
        if (sourceRowIdx === -1 || targetRowIdx === -1) return;
        const sourcePos = getDisplayPosition(rows[sourceRowIdx]);
        const targetPos = getDisplayPosition(rows[targetRowIdx]);
        if (!sourcePos || !targetPos) return;
        const sourceNode = graph.nodeOf(link.sourceProjectId, getNumericRef(link.source));
        const targetNode = graph.nodeOf(link.targetProjectId, getNumericRef(link.target));
        resolved.push({
            link,
            key: `${link.source}_${link.target}`,
            // Chain identity is project-scoped, unlike `key` above which is kept
            // as-is for hover identity.
            chainKey: `${sourceNode}->${targetNode}`,
            sourceNode,
            targetNode,
            sourceRowIdx,
            targetRowIdx,
            x1: leftPanelWidth + sourcePos.left + sourcePos.width,
            y1: headerOffset + (sourceRowIdx * rowHeight) + (rowHeight / 2),
            x2: leftPanelWidth + targetPos.left,
            y2: headerOffset + (targetRowIdx * rowHeight) + (rowHeight / 2),
            hasDateGap: getDateGap(rows[sourceRowIdx], rows[targetRowIdx]) > 1,
            // A line leaving a completed task renders in the completed grey
            sourceClosed: rows[sourceRowIdx].isClosed === true,
            colour: DEPENDENCY_COLOURS[link.color] || DEPENDENCY_COLOURS.gray
        });
    });

    const resolvedByChainKey = new Map(resolved.map(r => [r.chainKey, r]));

    // The whole chain running through a link, both directions.
    //
    // A dead link constrains nothing, so it terminates the walk and is never
    // part of a chain - hovering one isolates just itself, as before. Iterative
    // with a visited set, so a cycle in malformed data cannot hang the render.
    //
    // Limitation: a hop that cannot be drawn has no row, so its source's closed
    // state is unknown and it is counted as hidden rather than treated as a
    // chain break.
    const chainFor = (start) => {
        if (!start) return { keys: new Set(), hidden: 0 };
        if (start.sourceClosed) return { keys: new Set([start.chainKey]), hidden: 0 };

        const keys = new Set([start.chainKey]);
        const seen = new Set();
        const stack = [[start.sourceNode, 'up'], [start.targetNode, 'down']];
        let hidden = 0;

        while (stack.length) {
            const [node, dir] = stack.pop();
            const mark = `${dir}:${node}`;
            if (seen.has(mark)) continue;
            seen.add(mark);

            const edges = (dir === 'up' ? graph.inbound : graph.outbound).get(node) || [];
            for (const edge of edges) {
                const res = resolvedByChainKey.get(edge.chainKey);
                if (res?.sourceClosed) continue;   // dead link breaks the chain
                if (!keys.has(edge.chainKey)) {
                    keys.add(edge.chainKey);
                    if (!res) hidden++;
                }
                stack.push([dir === 'up' ? edge.sourceNode : edge.targetNode, dir]);
            }
        }
        return { keys, hidden };
    };

    // Every task touched by a chain, as node keys, for the parent's bar dimming.
    const chainNodes = (chainKeys) => {
        const nodes = new Set();
        graph.edges.forEach(edge => {
            if (!chainKeys.has(edge.chainKey)) return;
            nodes.add(edge.sourceNode);
            nodes.add(edge.targetNode);
        });
        return nodes;
    };

    // The chain as an indented tree for the tooltip.
    //
    // A chain is a DAG, not a list, so there is no single root: the roots are
    // the chain nodes nothing feeds into, and each is walked depth-first. A node
    // reached by a second path is printed once more as a greyed back-reference
    // rather than duplicating its whole subtree underneath.
    const TREE_VISIBLE = 15;   // lines shown before "+ N more"
    const TREE_HARD_MAX = 200; // runaway guard, independent of the display cap

    const chainTree = (start, chainKeys) => {
        const label = new Map();
        const childrenOf = new Map();
        const hasInbound = new Set();
        const allNodes = [];

        graph.edges.forEach(edge => {
            if (!chainKeys.has(edge.chainKey)) return;
            const res = resolvedByChainKey.get(edge.chainKey);
            if (res) {
                label.set(edge.sourceNode, rows[res.sourceRowIdx].text);
                label.set(edge.targetNode, rows[res.targetRowIdx].text);
            }
            if (!childrenOf.has(edge.sourceNode)) childrenOf.set(edge.sourceNode, []);
            childrenOf.get(edge.sourceNode).push(edge.targetNode);
            hasInbound.add(edge.targetNode);
            allNodes.push(edge.sourceNode, edge.targetNode);
        });

        const nodes = new Set(allNodes);
        // A pure cycle has no root; fall back to where the cursor is.
        let roots = [...nodes].filter(n => !hasInbound.has(n));
        if (roots.length === 0) roots = [start.sourceNode];

        const lines = [];
        const printed = new Set();
        // guides[] carries one mark per FORK ancestor - 'line' where that fork
        // still has siblings below it (so the run continues past this row) and
        // 'blank' where it does not. connector is this row's own elbow.
        // Held as data rather than a prepared string so the window rebase below
        // can drop the outer levels without leaving dangling guides.
        //
        // Only a fork adds a level. A linear run stays in its column: template
        // chains are mostly sequential, and one indent per hop turned a 12-hop
        // chain into a staircase that left no width for the labels.
        const walk = (node, guides, connector) => {
            if (lines.length >= TREE_HARD_MAX) return;
            const repeat = printed.has(node);
            lines.push({
                node,
                depth: guides.length + (connector ? 1 : 0),
                repeat,
                guides,
                connector,
                label: label.get(node) || '(hidden task)',
                current: node === start.sourceNode || node === start.targetNode
            });
            if (repeat) return;
            printed.add(node);
            const children = childrenOf.get(node) || [];
            // This row's label column: its guides plus its own elbow's cell.
            const cols = connector === null
                ? guides
                : [...guides, connector === 'tee' ? 'line' : 'blank'];
            if (children.length === 1) {
                walk(children[0], cols, null);
            } else {
                children.forEach((child, i) =>
                    walk(child, cols, i === children.length - 1 ? 'ell' : 'tee'));
            }
        };
        roots.forEach(root => walk(root, [], null));

        // Window the display around the hovered link so it is always in view.
        if (lines.length <= TREE_VISIBLE) return { lines, more: 0 };
        const firstCurrent = Math.max(0, lines.findIndex(l => l.current));
        const startIdx = Math.min(
            Math.max(0, firstCurrent - 3),
            lines.length - TREE_VISIBLE
        );
        const shown = lines.slice(startIdx, startIdx + TREE_VISIBLE);
        // Rebase the indent to the window. Depths are absolute, so a window deep
        // in a long chain would otherwise start at 20-odd levels of padding and
        // make the card wider than the screen. The guides are trimmed by the
        // same amount so no vertical run is left dangling from a cut ancestor.
        const baseDepth = Math.min(...shown.map(l => l.depth));
        return {
            lines: shown.map(l => ({
                ...l,
                depth: l.depth - baseDepth,
                guides: l.guides.slice(baseDepth),
                // A line promoted to the top of the window is a root there, so
                // it loses its elbow - keeping it would draw it in the same
                // column as its own child.
                connector: l.depth - baseDepth === 0 ? null : l.connector
            })),
            more: lines.length - TREE_VISIBLE
        };
    };

    const hoveredResolved = hoveredLinkKey === null
        ? null
        : resolved.find(r => r.key === hoveredLinkKey) || null;
    const chain = hoveredResolved ? chainFor(hoveredResolved) : { keys: new Set(), hidden: 0 };

    // Everything outside the hovered link's chain drops back.
    const dimmed = (r) => hoveredLinkKey !== null && !chain.keys.has(r.chainKey);
    const inChain = (r) => chain.keys.has(r.chainKey);

    // Corridor = links sharing the departure vertical
    const corridors = {};
    resolved.forEach(r => {
        const ck = Math.round(r.x1 + stubLength);
        if (!corridors[ck]) corridors[ck] = [];
        corridors[ck].push(r);
    });

    // --- Departure corridors: laned and placed ---
    // Crossing-free order: the shortest hop exits first, so it takes the
    // OUTERMOST lane and peels off without cutting siblings
    const laneOf = {};
    const corridorBase = {};
    const laneStepOf = {};
    const uTurnRowOf = {};
    Object.values(corridors).forEach(group => {
        const n = group.length;
        const step = n > 1 ? Math.min(laneStepMax, corridorCap / (n - 1)) : 0;
        const byHop = [...group].sort((a, b) =>
            Math.abs(b.targetRowIdx - b.sourceRowIdx) - Math.abs(a.targetRowIdx - a.sourceRowIdx) ||
            String(a.link.target).localeCompare(String(b.link.target)));
        byHop.forEach((r, i) => { laneOf[r.key] = i; });   // 0 = innermost = longest hop

        // Least-occlusion placement: scan a window right of the source end and
        // take the leftmost x whose comb passes behind the fewest bars over
        // the corridor's full vertical span
        const x1 = group[0].x1;
        const combWidth = step * (n - 1);
        const yMin = Math.min(...group.map(r => Math.min(r.y1, r.y2)));
        const yMax = Math.max(...group.map(r => Math.max(r.y1, r.y2)));
        const rowFrom = Math.max(0, Math.floor((yMin - headerOffset) / rowHeight));
        const rowTo = Math.min(rows.length - 1, Math.ceil((yMax - headerOffset) / rowHeight));
        let best = null;
        const scanStep = Math.max(2, 4 * s);
        for (let off = scanMin; off <= scanMax; off += scanStep) {
            const bx = x1 + off;
            let hits = 0;
            for (let ri = rowFrom; ri <= rowTo; ri++) {
                const pos = getDisplayPosition(rows[ri]);
                if (!pos) continue;
                const barL = leftPanelWidth + pos.left;
                const barR = barL + pos.width;
                if (bx + combWidth >= barL && bx <= barR) hits++;
            }
            if (hits === 0) { best = { bx, hits }; break; }  // leftmost clear channel
            if (!best || hits < best.hits) best = { bx, hits };
        }
        // No-gap corridor-mates share ONE U-turn at the LOWEST source's row:
        // an upper line never folds in on its own - it rides the shared
        // vertical past the lower bar and turns with it, joined (Bill,
        // 2026-07-29: same-end-date sources depending down).
        //
        // A corridor is every link whose SOURCE BAR FINISHES ON THE SAME DAY -
        // nothing more. Same-day finishes are ordinary (Friday finishes, wave
        // boundaries, milestone dates), so a corridor routinely holds links
        // from unrelated chains. The rule above only holds for a link that
        // actually travels DOWN TO OR PAST the shared row; one whose target
        // sits above it was being dragged down to a row it has no dependency
        // with and back up again. So the shared row is OFFERED, not imposed:
        // a link that does not reach it keeps its own source row and turns
        // locally.
        //
        // The boundary case - a mate whose TARGET IS the lower source's own
        // row - is admitted here and then pulled one row up by the clamp in
        // the path builder, so it arrives at that bar from above instead of
        // dropping past it and climbing back. The two rules agree everywhere
        // else; here the clamp deliberately wins.
        const noGap = group.filter(m => !m.hasDateGap);
        const uTurnRow = noGap.length > 0 ? Math.max(...noGap.map(m => m.sourceRowIdx)) : null;
        group.forEach(r => {
            corridorBase[r.key] = best.bx;
            laneStepOf[r.key] = step;
            if (uTurnRow !== null && r.targetRowIdx >= uTurnRow) uTurnRowOf[r.key] = uTurnRow;
        });
    });

    // --- Descent (arrival) corridors for no-gap paths ---
    // Opposite ordering to departure: the FIRST target row takes the lane
    // CLOSEST to the boxes, so each line turns off as the rightmost
    // still-active and never crosses a sibling
    const arrLaneOf = {};
    const arrStepOf = {};
    const descentCorridors = {};
    resolved.filter(r => !r.hasDateGap).forEach(r => {
        const k = Math.round(r.x2);
        if (!descentCorridors[k]) descentCorridors[k] = [];
        descentCorridors[k].push(r);
    });
    Object.values(descentCorridors).forEach(group => {
        const n = group.length;
        // Normal lane behaviour on the descent: every line gets its own lane
        // (the step is zoom-scaled, so squish still collapses them). First
        // box takes the closest lane.
        const step = n > 1 ? Math.min(laneStepMax, corridorCap / (n - 1)) : 0;
        group.sort((a, b) => a.targetRowIdx - b.targetRowIdx ||
            String(a.link.source).localeCompare(String(b.link.source)));
        group.forEach((r, i) => {
            arrLaneOf[r.key] = i;   // 0 = first box = closest lane
            arrStepOf[r.key] = step;
        });
    });

    // --- Port fan-out (per source bar) and arrival fan-in (per target bar) ---
    const portOffset = {};
    const bySource = {};
    resolved.forEach(r => {
        const k = `${r.link.source}|${r.link.sourceProjectId || ''}`;
        if (!bySource[k]) bySource[k] = [];
        bySource[k].push(r);
    });
    Object.values(bySource).forEach(group => {
        // Top port goes to the line heading highest, keeping the fan untangled
        group.sort((a, b) => (a.targetRowIdx - a.sourceRowIdx) - (b.targetRowIdx - b.sourceRowIdx) ||
            String(a.link.target).localeCompare(String(b.link.target)));
        const offs = fanOffsets(group.length);
        group.forEach((r, i) => { portOffset[r.key] = offs[i]; });
    });

    const arrivalOffset = {};
    const byTargetBar = {};
    resolved.forEach(r => {
        const k = `${r.link.target}|${r.link.targetProjectId || ''}`;
        if (!byTargetBar[k]) byTargetBar[k] = [];
        byTargetBar[k].push(r);
    });
    Object.values(byTargetBar).forEach(group => {
        group.sort((a, b) => (a.sourceRowIdx - a.targetRowIdx) - (b.sourceRowIdx - b.targetRowIdx) ||
            String(a.link.source).localeCompare(String(b.link.source)));
        const offs = fanOffsets(group.length);
        group.forEach((r, i) => { arrivalOffset[r.key] = offs[i]; });
    });

    const clickHandler = (link) => (e) => {
        if (readOnly) return;
        if (e.ctrlKey || e.metaKey) {
            if (onDependencyColorChange) {
                setColorPopup({ link, x: e.clientX, y: e.clientY });
            }
            return;
        }
        if (window.confirm('Delete this dependency?')) {
            setPendingLinks(prev => ({
                ...prev,
                removed: [...prev.removed, { source: link.source, target: link.target }]
            }));
            onDependencyDelete?.(link);
        }
    };

    return (
        <svg className="absolute top-0 left-0 pointer-events-none" style={svgStyle}>
            {resolved.map(r => {
                const py = r.y1 + (portOffset[r.key] || 0);
                const ty = r.y2 + (arrivalOffset[r.key] || 0);
                const laneX = corridorBase[r.key] + (laneOf[r.key] || 0) * laneStepOf[r.key];
                let pts;
                if (r.hasDateGap) {
                    // Clamp so a wide comb can never push past the arrowhead
                    const cx = Math.min(laneX, r.x2 - 8);
                    pts = [[r.x1, py], [cx, py], [cx, ty], [r.x2, ty]];
                } else {
                    // No-gap hop: U-turn under the source row, then descend a
                    // second corridor to the target. The U-turn is deliberately
                    // COLLAPSED - all lines share one exit x and one gy, fully
                    // overlapping through the turn, and only separate at the
                    // descent corridor where each peels off.
                    const ux = corridorBase[r.key];
                    // Exactly on the row boundary = dead centre of the gap
                    // between bars; any nudge puts it on a bar edge.
                    //
                    // Backstop, independent of how the corridor chose to share
                    // a row: the turn NEVER goes below this link's own span. A
                    // grouping change can widen a corridor; it must not be able
                    // to send a line down through rows the link has no
                    // dependency with. The floor is targetRowIdx - 1, the row
                    // boundary immediately ABOVE the target, so the descent
                    // still arrives at the target from above rather than
                    // overshooting it and climbing back.
                    const turnRow = uTurnRowOf[r.key] !== undefined ? uTurnRowOf[r.key] : r.sourceRowIdx;
                    const ownSpanFloor = Math.max(r.sourceRowIdx, r.targetRowIdx - 1);
                    const gy = headerOffset + (Math.min(turnRow, ownSpanFloor) * rowHeight) + rowHeight;
                    const ax = r.x2 - Math.max(8, stubLength * s)
                        - (arrLaneOf[r.key] || 0) * (arrStepOf[r.key] || 0);
                    if (ax >= r.x1 + 2) {
                        // Descent lane sits at/right of the bar end: just drop -
                        // no pointless right-hook-and-back U-turn at the exit
                        pts = [[r.x1, py], [ax, py], [ax, ty], [r.x2, ty]];
                    } else {
                        pts = [[r.x1, py], [ux, py], [ux, gy], [ax, gy], [ax, ty], [r.x2, ty]];
                    }
                }
                const d = roundedPath(pts, cornerR);
                // Squished: neutral grey at rest, own colour revealed on hover.
                // Dead (source closed): pale, and dashed until hovered - hover
                // only solidifies the dash so the route can be followed. Colour
                // and weight never change, so an inert link cannot become the
                // most prominent thing on the chart just because the cursor
                // crossed it.
                const isHovered = hoveredLinkKey === r.key;
                // The squish reveal follows the CHAIN, not just the hovered
                // link, so a highlighted run reads as one colour at low zoom.
                const lineColour = r.sourceClosed
                    ? DEAD_COLOUR
                    : (s === 0 && !inChain(r) ? SQUISH_COLOUR : r.colour);
                const arrowColour = r.sourceClosed ? DEAD_COLOUR : r.colour;
                return (
                    <g
                        key={r.link.id || r.key}
                        className="cursor-pointer"
                        style={{ pointerEvents: 'auto', opacity: dimmed(r) ? 0.25 : 1 }}
                        onClick={clickHandler(r.link)}
                        {...hoverProps(r)}
                    >
                        <path
                            d={d}
                            stroke={lineColour}
                            strokeWidth={strokeW(r)}
                            strokeDasharray={r.sourceClosed && !isHovered ? DEAD_DASH : undefined}
                            fill="none"
                        />
                        <polygon
                            points={`${r.x2},${ty} ${r.x2 - 8},${ty - 4} ${r.x2 - 8},${ty + 4}`}
                            fill={arrowColour}
                        />
                        <path d={d} stroke="transparent" strokeWidth="10" fill="none" />
                    </g>
                );
            })}
        </svg>
    );
};

export default DependencyArrows;
